import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["textarea", "overlay", "submitButton", "invalidFeedback", "serverFeedback", "nonWhitespaceCharacterCount"]
  static values = { validTags: Array, serverInvalid: Boolean, maxNonWhitespaceCharacters: Number }

  connect() {
    this.serverRejectedBody = this.serverInvalidValue ? this.textareaTarget.value : null
    this.updateContent()
  }

  insertMergeTag(event) {
    event.preventDefault()
    const tag = event.currentTarget.dataset.tag + " "

    this.textareaTarget.focus()
    const start = this.textareaTarget.selectionStart
    const end = this.textareaTarget.selectionEnd

    const before = this.textareaTarget.value.slice(0, start)
    const after = this.textareaTarget.value.slice(end)
    this.textareaTarget.value = before + tag + after
    this.textareaTarget.selectionStart = this.textareaTarget.selectionEnd = start + tag.length

    this.textareaTarget.dispatchEvent(new InputEvent('input', { bubbles: true }))
  }

  updateContent() {
    this.updateServerValidation()
    this.updateOverlay()
    this.updateCharacterCount()
    this.updateValidationState()
  }

  updateServerValidation() {
    if (!this.hasServerFeedbackTarget) return

    const hasServerError = this.hasActiveServerError()
    this.serverInvalidValue = hasServerError
    this.serverFeedbackTarget.hidden = !hasServerError
    this.serverFeedbackTarget.classList.toggle('d-block', hasServerError)

    const feedbackId = this.serverFeedbackTarget.id
    const descriptionIds = (this.textareaTarget.getAttribute('aria-describedby') || '')
      .split(/\s+/)
      .filter(id => id && id !== feedbackId)

    if (hasServerError) descriptionIds.push(feedbackId)
    this.textareaTarget.setAttribute('aria-describedby', descriptionIds.join(' '))
  }

  updateOverlay() {
    const text = this.textareaTarget.value
    this.overlayTarget.innerHTML = this.highlightTags(text)
    this.syncScroll()
  }

  syncScroll() {
    this.overlayTarget.scrollTop = this.textareaTarget.scrollTop
    this.overlayTarget.scrollLeft = this.textareaTarget.scrollLeft
  }

  updateValidationState() {
    const hasInvalidTags = this.overlayTarget.querySelector('.invalid-tag') !== null
    const isOverCharacterLimit = this.currentNonWhitespaceCharacterCount > this.maxNonWhitespaceCharactersValue
    const isInvalid = hasInvalidTags || isOverCharacterLimit || this.hasActiveServerError()

    this.submitButtonTarget.disabled = hasInvalidTags
    this.submitButtonTarget.setAttribute('aria-disabled', hasInvalidTags.toString())
    this.textareaTarget.setAttribute('aria-invalid', isInvalid.toString())
    this.invalidFeedbackTarget.hidden = !hasInvalidTags
    this.invalidFeedbackTarget.classList.toggle('d-block', hasInvalidTags)
  }

  updateCharacterCount() {
    this.currentNonWhitespaceCharacterCount = this.countNonWhitespaceCharacters(this.textareaTarget.value)
    const isOverCharacterLimit = this.currentNonWhitespaceCharacterCount > this.maxNonWhitespaceCharactersValue

    this.nonWhitespaceCharacterCountTarget.textContent = `${this.currentNonWhitespaceCharacterCount} / ${this.maxNonWhitespaceCharactersValue} non-whitespace characters`
    this.nonWhitespaceCharacterCountTarget.classList.toggle('text-danger', isOverCharacterLimit)
    this.nonWhitespaceCharacterCountTarget.classList.toggle('fw-semibold', isOverCharacterLimit)
  }

  countNonWhitespaceCharacters(text) {
    return Array.from(text).filter(character => !/\p{White_Space}/u.test(character)).length
  }

  hasActiveServerError() {
    return this.serverRejectedBody !== null && this.textareaTarget.value === this.serverRejectedBody
  }

  highlightTags(text) {
    text = this.escapeHtml(text)
    return text.replace(/{{[^{}]+}}/g, (match) => {
      const isValid = this.validTagsValue.includes(match)
      const className = isValid ? 'valid-tag' : 'invalid-tag'
      return `<span class="${className}">${match}</span>`
    })
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
